// ─── Types ─────────────────────────────────────────────────────────────────────

export interface MarketingLead {
    id: string;
    phone: string;
    email?: string;
    name?: string;
    state: string;
    decisionRole?: string;
    dailyVolume?: string;
    followUpBehavior?: string;
    intentLevel?: string;
    qualificationRole?: string;
    qualificationVolume?: string;
    clinicName?: string;
    clinicCity?: string;
    clinicCountry?: string;
    isQualified: boolean;
    disqualificationReason?: string;
    trialOrganizationId?: string;
    trialStartedAt?: string;
    trialEndsAt?: string;
    trialConversationsCount: number;
    trialConversationsLimit: number;
    reminderDay12Sent?: boolean;
    reminderDay12SentAt?: string;
    followup24hSent?: boolean;
    followup24hSentAt?: string;
    followup48hSent?: boolean;
    followup48hSentAt?: string;
    silenceFollowupSent?: boolean;
    silenceFollowupSentAt?: string;
    silenceFollowup2Sent?: boolean;
    silenceFollowup2SentAt?: string;
    usageWarning80Sent?: boolean;
    usageWarning80SentAt?: string;
    usageWarning95Sent?: boolean;
    usageWarning95SentAt?: string;
    usageLimitSent?: boolean;
    usageLimitSentAt?: string;
    convertedAt?: string;
    conversionPlan?: string;
    followupNotes?: string;
    followupNextDate?: string;
    leadSummary?: string;
    outreachStatus?: string;
    outreachReplied?: boolean;
    outreachTemplate?: string;
    outreachSentAt?: string;
    source: string;
    adCampaignId?: string;
    updatedAt?: string;
    createdAt: string;
}

export interface Stats {
    total: number;
    byState: Record<string, number>;
    conversionRate: number;
    activeTrials: number;
}

export interface OrgInfo {
    id: string;
    name: string;
    plan: string;
    status: string;
    isTrial: boolean;
    billingPeriodEnd?: string;
    createdAt: string;
}

export interface Feedback {
    id: string;
    message: string;
    user_id?: string;
    user_email?: string;
    user_name?: string;
    organization_id?: string;
    created_at: string;
}

export interface Message {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    createdAt: string;
}

// ─── State config ──────────────────────────────────────────────────────────────

export const stateConfig: Record<string, { label: string; color: string; bg: string; border: string; icon: string }> = {
    NEW_LEAD:               { label: 'Nuevo',              color: 'text-slate-600',   bg: 'bg-slate-100',   border: 'border-slate-200',  icon: 'fiber_new' },
    PRESENTATION:           { label: 'Presentación',       color: 'text-blue-600',    bg: 'bg-blue-100',    border: 'border-blue-200',   icon: 'waving_hand' },
    QUALIFICATION_ROLE:     { label: 'Rol',                color: 'text-amber-600',   bg: 'bg-amber-100',   border: 'border-amber-200',  icon: 'badge' },
    QUALIFICATION_VOLUME:   { label: 'Volumen',            color: 'text-amber-600',   bg: 'bg-amber-100',   border: 'border-amber-200',  icon: 'bar_chart' },
    QUALIFICATION_FOLLOWUP: { label: 'Seguimiento',        color: 'text-amber-600',   bg: 'bg-amber-100',   border: 'border-amber-200',  icon: 'reply' },
    QUALIFICATION_INTENT:   { label: 'Intención',          color: 'text-amber-600',   bg: 'bg-amber-100',   border: 'border-amber-200',  icon: 'flag' },
    LEAD_EVALUATION:        { label: 'Evaluación',         color: 'text-purple-600',  bg: 'bg-purple-100',  border: 'border-purple-200', icon: 'analytics' },
    CONDITIONAL_VALIDATION: { label: 'Validación',         color: 'text-indigo-600',  bg: 'bg-indigo-100',  border: 'border-indigo-200', icon: 'help_center' },
    DEMO_FLOW:              { label: 'Demo',               color: 'text-gray-500',    bg: 'bg-gray-100',    border: 'border-gray-200',   icon: 'slideshow' },
    TRIAL_ACTIVATION:       { label: 'Activando Trial',    color: 'text-indigo-600',  bg: 'bg-indigo-100',  border: 'border-indigo-200', icon: 'rocket_launch' },
    TRIAL_ACTIVE:           { label: 'Trial Activo',       color: 'text-emerald-600', bg: 'bg-emerald-100', border: 'border-emerald-200',icon: 'science' },
    VALUE_EVENT_TRIGGER:    { label: 'Evento de Valor',    color: 'text-teal-600',    bg: 'bg-teal-100',    border: 'border-teal-200',   icon: 'stars' },
    TRIAL_CLOSURE:          { label: 'Cierre Trial',       color: 'text-orange-600',  bg: 'bg-orange-100',  border: 'border-orange-200', icon: 'timer' },
    PRO_OFFER:              { label: 'Oferta Pro',         color: 'text-cyan-600',    bg: 'bg-cyan-100',    border: 'border-cyan-200',   icon: 'local_offer' },
    OBJECTION_HANDLING:     { label: 'Objeción',           color: 'text-rose-600',    bg: 'bg-rose-100',    border: 'border-rose-200',   icon: 'help' },
    DOWNGRADE_OFFER:        { label: 'Oferta Básico',      color: 'text-yellow-700',  bg: 'bg-yellow-100',  border: 'border-yellow-200', icon: 'arrow_downward' },
    PLAN_ACTIVE:            { label: 'Cliente Activo',     color: 'text-green-700',   bg: 'bg-green-100',   border: 'border-green-200',  icon: 'workspace_premium' },
    END_GRACEFULLY:         { label: 'Finalizado',         color: 'text-gray-500',    bg: 'bg-gray-100',    border: 'border-gray-200',   icon: 'sentiment_neutral' },
};

export const funnelGroups = [
    { label: 'Adquisición y Filtro', color: 'text-slate-500', states: ['NEW_LEAD', 'PRESENTATION', 'QUALIFICATION_ROLE', 'QUALIFICATION_VOLUME', 'QUALIFICATION_FOLLOWUP', 'QUALIFICATION_INTENT', 'LEAD_EVALUATION', 'CONDITIONAL_VALIDATION', 'DEMO_FLOW'] },
    { label: 'Trial Plan Pro', color: 'text-emerald-600', states: ['TRIAL_ACTIVATION', 'TRIAL_ACTIVE', 'VALUE_EVENT_TRIGGER'] },
    { label: 'Cierre', color: 'text-orange-600', states: ['TRIAL_CLOSURE', 'PRO_OFFER', 'OBJECTION_HANDLING', 'DOWNGRADE_OFFER'] },
    { label: 'Convertido', color: 'text-green-600', states: ['PLAN_ACTIVE'] },
    { label: 'Finalizado', color: 'text-gray-400', states: ['END_GRACEFULLY'] },
];

export const trialStates = new Set(['TRIAL_ACTIVE', 'VALUE_EVENT_TRIGGER', 'TRIAL_CLOSURE', 'PRO_OFFER', 'OBJECTION_HANDLING', 'DOWNGRADE_OFFER']);

// ─── Helpers ───────────────────────────────────────────────────────────────────

export function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    return `hace ${Math.floor(hrs / 24)}d`;
}

export function daysStuck(lead: MarketingLead): number {
    const ref = lead.updatedAt || lead.createdAt;
    return Math.floor((Date.now() - new Date(ref).getTime()) / 86_400_000);
}

const STUCK_THRESHOLD: Record<string, number> = {
    NEW_LEAD: 1, PRESENTATION: 2, QUALIFICATION_ROLE: 3, QUALIFICATION_VOLUME: 3,
    QUALIFICATION_FOLLOWUP: 3, QUALIFICATION_INTENT: 3, QUALIFICATION_DENTAL: 3,
    LEAD_EVALUATION: 2, CONDITIONAL_VALIDATION: 2, DEMO_FLOW: 2,
    TRIAL_CLOSURE: 1, PRO_OFFER: 1, OBJECTION_HANDLING: 2, DOWNGRADE_OFFER: 1,
};

export function isStuck(lead: MarketingLead): boolean {
    const threshold = STUCK_THRESHOLD[lead.state];
    if (!threshold) return false;
    return daysStuck(lead) >= threshold;
}

export type UrgencyReason = 'trial_expira_pronto' | 'uso_alto' | 'estancado';

export function getUrgentReasons(lead: MarketingLead): UrgencyReason[] {
    const reasons: UrgencyReason[] = [];
    const now = new Date();
    if (lead.trialEndsAt) {
        const msLeft = new Date(lead.trialEndsAt).getTime() - now.getTime();
        if (msLeft > 0 && msLeft < 48 * 3600 * 1000) reasons.push('trial_expira_pronto');
    }
    if (lead.trialConversationsLimit > 0 &&
        lead.trialConversationsCount / lead.trialConversationsLimit >= 0.8 &&
        ['TRIAL_ACTIVE', 'VALUE_EVENT_TRIGGER'].includes(lead.state)) {
        reasons.push('uso_alto');
    }
    if (isStuck(lead) && lead.state !== 'PLAN_ACTIVE' && lead.state !== 'END_GRACEFULLY') {
        reasons.push('estancado');
    }
    return reasons;
}

export const URGENCY_LABEL: Record<UrgencyReason, { label: string; cls: string; icon: string }> = {
    trial_expira_pronto: { label: 'Trial vence pronto', cls: 'bg-rose-100 text-rose-700 border-rose-200', icon: 'timer' },
    uso_alto:            { label: 'Uso alto', cls: 'bg-orange-100 text-orange-700 border-orange-200', icon: 'speed' },
    estancado:           { label: 'Sin actividad', cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: 'hourglass_empty' },
};

export function isMetaSource(lead: MarketingLead): boolean {
    return !!(lead.adCampaignId || ['meta', 'facebook', 'instagram', 'fb_ad', 'meta_ad'].some(k => lead.source?.toLowerCase().includes(k)));
}

/** Converts UTC ISO string to local datetime-local value */
export function toLocalDatetimeInput(utcStr: string | null | undefined): string {
    if (!utcStr) return '';
    const d = new Date(utcStr);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
